/**
 * ConcurrentCaseEditGuard same-browser fast-path (docs/AUTOSAVE_DETAILS.md §17).
 *
 * The server tab-lease + TTL is the reliable, cross-browser source of truth. But for the common
 * case of the SAME user opening a case in two tabs of the SAME browser, a `BroadcastChannel` lets
 * those tabs notify each other instantly with zero server calls:
 *
 * - `announceTakeover` is broadcast whenever a tab acquires the marker (opening the case, or
 *   clicking "take back control"). Every OTHER tab editing that same case receives it and, since
 *   a different tab now holds it, surfaces the takeover modal immediately (no ~15s poll wait).
 *   BroadcastChannel does not echo a message back to its sender, so the acquiring tab never
 *   modals itself -- only the tab that just lost the marker does. This one-shot-on-acquire model
 *   (rather than a continuous "I'm holding" heartbeat) is deliberate: two tabs both continuously
 *   asserting "holding" would each modal the other.
 * - `announceReleased` is broadcast on clean exit (clear/close) so peers (and the homepage) can
 *   refresh promptly.
 *
 * Scope: same-origin, same-browser only. Different browsers/devices are handled entirely by the
 * server lease + poll; crash detection (no clean release fires) is handled by the ~60s server TTL
 * for both same- and cross-browser. This channel is purely a latency/efficiency optimization.
 */

const CHANNEL_NAME = 'calc-case-editing';

export interface PresenceMessage {
    type: 'takeover' | 'released' | 'saved';
    caseId: string;
    tabId: string;
    userId?: string;
    firstName?: string;
    lastName?: string;
    // For 'saved': the case's new updated_at, so a homepage tab can refresh its "last saved X ago"
    // for that row locally (no server call).
    updatedAt?: string;
}

type PresenceHandler = (msg: PresenceMessage) => void;

function getChannel(): BroadcastChannel | null {
    try {
        if (typeof BroadcastChannel === 'undefined') return null;
        return new BroadcastChannel(CHANNEL_NAME);
    } catch {
        return null;
    }
}

/**
 * Subscribe to presence messages from other tabs in this browser. Returns an unsubscribe fn.
 * No-op (returns a no-op unsubscribe) where BroadcastChannel is unavailable.
 */
export function subscribeToPresence(handler: PresenceHandler): () => void {
    const channel = getChannel();
    if (!channel) return () => {};

    const onMessage = (e: MessageEvent) => {
        const data = e.data as PresenceMessage | undefined;
        if (data && (data.type === 'takeover' || data.type === 'released' || data.type === 'saved') && data.caseId && data.tabId) {
            handler(data);
        }
    };
    channel.addEventListener('message', onMessage);
    return () => {
        try {
            channel.removeEventListener('message', onMessage);
            channel.close();
        } catch { /* ignore */ }
    };
}

function post(msg: PresenceMessage): void {
    const channel = getChannel();
    if (!channel) return;
    try {
        channel.postMessage(msg);
    } finally {
        // One-shot: open a fresh channel per post so we never leak a long-lived sender.
        try { channel.close(); } catch { /* ignore */ }
    }
}

/** Broadcast that this tab just acquired the editing marker for `caseId` (open / take-back). */
export function announceTakeover(caseId: string, tabId: string, userId?: string, firstName?: string, lastName?: string): void {
    post({ type: 'takeover', caseId, tabId, userId, firstName, lastName });
}

/** Broadcast that this tab released the editing marker for `caseId` (clean exit). */
export function announceReleased(caseId: string, tabId: string): void {
    post({ type: 'released', caseId, tabId });
}

/**
 * Broadcast that this tab just saved `caseId` (autosave succeeded), carrying the new updated_at.
 * A same-browser homepage tab uses this to refresh that row's "last saved X ago" instantly,
 * without a server round-trip.
 */
export function announceSaved(caseId: string, tabId: string, updatedAt?: string): void {
    post({ type: 'saved', caseId, tabId, updatedAt });
}
